import { MAX_CART_QUANTITY } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/constants';

/** Extracts the rendered row index from the extended cell context injected by OrderEntryTableRow. */
export const getRenderedRowIndex = (
  context: Record<string, unknown>,
  fallbackIndex: number,
): number =>
  typeof context.index === 'number' ? context.index : fallbackIndex;

/**
 * Clamps a committed order-entry quantity to an item's min/max constraints.
 *
 * A min constraint of 0 means "no minimum"; a max constraint of 0 falls back
 * to MAX_CART_QUANTITY. A quantity of 0 or less means "remove from cart" and
 * is never raised to the minimum. In-range values, including decimals, are
 * returned unchanged.
 *
 * @param quantity - The committed quantity to clamp
 * @param minQuantity - The minimum allowed quantity, or 0 for no minimum
 * @param maxQuantity - The maximum allowed quantity, or 0 to fall back to MAX_CART_QUANTITY
 * @returns The quantity clamped into the allowed range
 *
 * @example
 * clampQuantityToLimits(50, 0, 10) // 10
 * clampQuantityToLimits(1, 3, 0) // 3
 * clampQuantityToLimits(0, 3, 10) // 0
 * clampQuantityToLimits(5, 2, 10) // 5
 */
export const clampQuantityToLimits = (
  quantity: number,
  minQuantity: number,
  maxQuantity: number,
): number => {
  const effectiveMax = maxQuantity > 0 ? maxQuantity : MAX_CART_QUANTITY;
  if (quantity > effectiveMax) {
    return effectiveMax;
  }
  if (minQuantity > 0 && quantity > 0 && quantity < minQuantity) {
    return minQuantity;
  }
  return quantity;
};
